/**
 * The public "board" route: one game card's prices, trimmed server-side.
 *
 *   GET /api/public/contests/<id>/board?kind=game|futures
 *     → upstream GET /flow/v1/contests/<id>/liquidity (org key)
 *     → { data: CardLiquidity }   (lib/marketsBoard.ts `trimForCard`)
 *
 * Why: the API can't narrow liquidity to one market (see FRICTION.md), so a busy game is
 * ~3.4 MB for the two prices a card shows. Trimming once at the edge, and caching the trimmed
 * answer for everyone, takes each card to a few KB and keeps card loads off the viewer's own
 * rate limit. Card prices are public market data, so signed-in viewers use it too.
 *
 * Pure and Workers-safe (no DOM, no Node APIs): shared by the Pages Function, the Vite dev
 * middleware, and the browser, so all three agree on the route and the trim.
 */
import { trimForCard, type CardLiquidity } from "./marketsBoard.js";
import type { PositionLike } from "./books.js";
import { ID } from "./publicFeed.js";

export const BOARD_TTL = 30;

export type BoardKind = "game" | "futures";

export interface BoardRequest {
  contestId: string;
  kind: BoardKind;
  /** Upstream path relative to `/flow/v1/`. */
  upstreamPath: string;
  /** Cache-key path: the kind is part of it (the two trims differ). */
  cachePath: string;
}

const BOARD_RE = new RegExp(`^contests/(${ID})/board$`);

/** `contests/<id>/board` (+ `kind`) → the request to make, or null when it isn't a board route. */
export function resolveBoardRequest(path: string, search: URLSearchParams | string = ""): BoardRequest | null {
  const clean = path.replace(/^\/+/, "").replace(/\/+$/, "");
  const m = BOARD_RE.exec(clean);
  if (!m) return null;
  const contestId = m[1];
  const q = typeof search === "string" ? new URLSearchParams(search) : search;
  const kind: BoardKind = q.get("kind") === "futures" ? "futures" : "game";
  const id = encodeURIComponent(contestId);
  return { contestId, kind, upstreamPath: `contests/${id}/liquidity`, cachePath: `contests/${id}/board/${kind}` };
}

/** The upstream liquidity envelope → the board envelope. Throws on a shape it can't read. */
export function boardFromLiquidity(body: unknown, kind: BoardKind): { data: CardLiquidity } {
  const rows = (body as { data?: unknown } | null)?.data;
  if (!Array.isArray(rows)) throw new Error("liquidity response has no data array");
  return { data: trimForCard(rows as PositionLike[], kind === "game") };
}

/** Same-origin URL the browser fetches for a card. */
export function boardUrl(contestId: string, kind: BoardKind): string {
  return `/api/public/contests/${encodeURIComponent(contestId)}/board${kind === "futures" ? "?kind=futures" : ""}`;
}
